/**
 * Protocol-v2 session store — one `WasmSessionRecord` per pairwise conversation.
 *
 * Invariants (SEC-CRY-003 / key-reuse safety):
 *  1. All record operations for a conversation are serialized (async mutex), so
 *     encrypt/decrypt never interleave and states are persisted in order.
 *  2. Ratchet state is persisted BEFORE the resulting ciphertext is returned to
 *     callers (and therefore before it can be published). If the process dies
 *     between encrypt and persist, that ciphertext never left the device, so
 *     re-deriving the same message key after reload is harmless.
 *  3. A decrypt commits the advanced state together with an inbound-journal row
 *     holding the plaintext (one IndexedDB transaction), so a consumed message
 *     key can never lose its message (review M2).
 *  4. Sessions are never silently forgotten (review H2): a copy of the replay
 *     cache lives outside the record; an unreadable record is quarantined, and
 *     its replacement is seeded with that cache and restarts, so old first-contact
 *     messages stay rejected. Chat deletion keeps the session.
 *  5. v1 state (unauthenticated handshake + symmetric chains) never imports.
 *  6. Only one unlocked instance runs at a time (see instanceLock.ts, review H3);
 *     this cache is therefore authoritative while unlocked.
 */
import {
  WasmSessionRecord,
  toHex,
  type WasmIdentity,
  type WasmInbound,
} from "@veto/crypto-bridge";
import {
  listSessionConversationIds,
  loadReplayGuard,
  loadSessionState,
  quarantineSessionRow,
  saveSessionRecordJson,
  sessionRowExists,
} from "@veto/storage";

export type PeerKeyBytes = { ed: Uint8Array; hybrid: Uint8Array };

export type SessionInfo = {
  has_session: boolean;
  role: "i" | "r" | null;
  confirmed: boolean;
  archived: number;
  created_at: number | null;
  restart_pending: boolean;
  ratchet: null | {
    can_send: boolean;
    sent_in_chain: number;
    skipped_keys: number;
    pq_steps_since: number;
    pq_last_at: number;
    pq_announcing: boolean;
  };
};

/** Journal row produced from a decrypted plaintext (stored sealed with the state). */
export type JournalWrite = (plaintext: Uint8Array) => { id: string; json: string };

function b64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]!);
  return btoa(s);
}

function unb64(text: string): Uint8Array {
  const bin = atob(text);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export class SessionStore {
  private readonly records = new Map<string, WasmSessionRecord>();
  /** Records created in memory that have never been persisted. */
  private readonly unsaved = new Set<string>();
  /** Last replay-cache copy written per conversation (skip redundant writes). */
  private readonly guards = new Map<string, string>();
  private readonly tails = new Map<string, Promise<void>>();
  private closed = false;

  constructor(
    readonly identity: WasmIdentity,
    private readonly selfEd: Uint8Array,
  ) {}

  /** Serialize `fn` behind every earlier operation on `convId`. */
  private exclusive<T>(convId: string, fn: () => Promise<T>): Promise<T> {
    const prev = this.tails.get(convId) ?? Promise.resolve();
    const run = prev.then(() => {
      if (this.closed) throw new Error("session store closed (vault locked)");
      return fn();
    });
    const tail = run.then(
      () => undefined,
      () => undefined,
    );
    this.tails.set(convId, tail);
    void tail.then(() => {
      if (this.tails.get(convId) === tail) this.tails.delete(convId);
    });
    return run;
  }

  private async load(convId: string, peerEd: Uint8Array): Promise<WasmSessionRecord> {
    const cached = this.records.get(convId);
    if (cached) {
      if (toHex(cached.peerEd()) === toHex(peerEd)) return cached;
      throw new Error("conversation is bound to a different peer");
    }
    let rec: WasmSessionRecord | null = null;
    const stored = await loadSessionState(convId);
    if (stored?.ratchetStateJson) {
      try {
        rec = WasmSessionRecord.importState(stored.ratchetStateJson);
      } catch {
        rec = null;
      }
      if (rec && toHex(rec.peerEd()) !== toHex(peerEd)) {
        rec.free();
        throw new Error("stored session belongs to a different peer");
      }
    }
    if (!rec) {
      const rowExists = await sessionRowExists(convId);
      const guard = await loadReplayGuard(convId);
      rec = new WasmSessionRecord(this.selfEd, peerEd);
      if (rowExists) await quarantineSessionRow(convId); // never overwrite silently
      if (guard) {
        try {
          rec.mergeReplayCache(unb64(guard));
          this.guards.set(convId, guard);
        } catch {
          /* corrupt guard: the restart below still converges */
        }
      }
      // Sessions existed before and are gone: start over with a flagged restart.
      if (rowExists || guard) rec.restartSession();
      this.unsaved.add(convId);
    }
    this.records.set(convId, rec);
    return rec;
  }

  private async persist(convId: string, rec: WasmSessionRecord, journal?: { id: string; json: string }): Promise<void> {
    const guard = b64(rec.replayCache());
    const guardChanged = this.guards.get(convId) !== guard;
    await saveSessionRecordJson(convId, rec.exportState(), {
      replayCacheB64: guardChanged ? guard : undefined,
      journal,
    });
    if (guardChanged) this.guards.set(convId, guard);
    this.unsaved.delete(convId);
  }

  /** A record that never held state and just failed: don't keep it around (L4). */
  private dropIfUnsaved(convId: string): void {
    if (!this.unsaved.has(convId)) return;
    this.records.get(convId)?.free();
    this.records.delete(convId);
    this.unsaved.delete(convId);
  }

  /** Encrypt for `peer`; returns a sealed envelope, persisted first (invariant 2). */
  encrypt(convId: string, peer: PeerKeyBytes, plaintext: Uint8Array): Promise<Uint8Array> {
    return this.exclusive(convId, async () => {
      const rec = await this.load(convId, peer.ed);
      const envelope = rec.encrypt(this.identity, peer.ed, peer.hybrid, plaintext);
      await this.persist(convId, rec);
      return envelope;
    });
  }

  /**
   * Decrypt an opened inbound message; success authenticates `peer`. With
   * `journal`, the plaintext is journaled atomically with the new state.
   */
  decrypt(convId: string, peer: PeerKeyBytes, inbound: WasmInbound, journal?: JournalWrite): Promise<Uint8Array> {
    return this.exclusive(convId, async () => {
      const rec = await this.load(convId, peer.ed);
      let plaintext: Uint8Array;
      try {
        plaintext = rec.decrypt(this.identity, peer.ed, peer.hybrid, inbound);
      } catch (err) {
        this.dropIfUnsaved(convId);
        throw err;
      }
      await this.persist(convId, rec, journal?.(plaintext));
      return plaintext;
    });
  }

  /**
   * Would this PREKEY authenticate against `peer`'s keys? Runs on a throw-away
   * record — nothing is stored. Used before raising a key-change alarm (L3).
   */
  probe(peer: PeerKeyBytes, inbound: WasmInbound): boolean {
    const scratch = new WasmSessionRecord(this.selfEd, peer.ed);
    try {
      scratch.decrypt(this.identity, peer.ed, peer.hybrid, inbound);
      return true;
    } catch {
      return false;
    } finally {
      scratch.free();
    }
  }

  info(convId: string, peerEd: Uint8Array): Promise<SessionInfo | null> {
    return this.exclusive(convId, async () => {
      const rec = await this.load(convId, peerEd);
      return JSON.parse(rec.info()) as SessionInfo;
    });
  }

  /** Recovery: the peer evidently holds a session we lost — restart ours. */
  restartSession(convId: string, peerEd: Uint8Array): Promise<void> {
    return this.exclusive(convId, async () => {
      const rec = await this.load(convId, peerEd);
      rec.restartSession();
      await this.persist(convId, rec);
    });
  }

  /** After a security change: ask the active session for an ML-KEM re-key. */
  async forcePqRekey(convId: string, peerEd: Uint8Array): Promise<void> {
    await this.exclusive(convId, async () => {
      const rec = await this.load(convId, peerEd);
      rec.forcePqRekey();
      await this.persist(convId, rec);
    });
  }

  /**
   * Device revocation (FR-DEV-003): retire every stored session — each next send
   * starts a fresh hybrid AKE — while keeping replay caches, so old PREKEYs can
   * never re-open a session (review H2). Runs under each conversation's lock (L4).
   */
  async retireAll(): Promise<number> {
    const ids = await listSessionConversationIds();
    let n = 0;
    for (const convId of ids) {
      await this.exclusive(convId, async () => {
        let rec = this.records.get(convId) ?? null;
        if (!rec) {
          const stored = await loadSessionState(convId);
          if (!stored?.ratchetStateJson) return;
          try {
            rec = WasmSessionRecord.importState(stored.ratchetStateJson);
          } catch {
            return;
          }
          this.records.set(convId, rec);
        }
        rec.retireSessions();
        await this.persist(convId, rec);
        n += 1;
      });
    }
    return n;
  }

  /** Forget in-memory records (durable state is kept). */
  clearCache(): void {
    for (const rec of this.records.values()) {
      try {
        rec.free();
      } catch {
        /* already freed */
      }
    }
    this.records.clear();
    this.unsaved.clear();
  }

  /** Vault lock: free WASM state and refuse further work. */
  close(): void {
    this.closed = true;
    this.clearCache();
  }

  /** Resolves once every queued operation has finished (lock / hand-over). */
  async drain(): Promise<void> {
    await Promise.all([...this.tails.values()]);
  }
}
